import { useState } from "react";

export interface Span { start: number; end: number; category?: string }

interface Props {
  previewText?: string;
  fallback?: string;
  spans?: Span[];
  /** If provided, the component renders from this full text and redacts `spans` in-place. */
  fullText?: string;
}

export function RedactedPreview({ previewText, spans, fullText, fallback }: Props) {
  // Render order:
  //   1) fullText + spans  →  selective redaction of just the sensitive segments
  //   2) previewText containing 【category】 markers  →  replace markers with redaction bars
  //   3) fallback masked preview (word-level dots)
  if (fullText && spans?.length) return <SelectiveRedact text={fullText} spans={spans} />;
  if (previewText && /【[^】]+】/.test(previewText)) return <MarkerRedact text={previewText} />;
  return <div className="sg-redact">{previewText || fallback || "—"}</div>;
}

function SelectiveRedact({ text, spans }: { text: string; spans: Span[] }) {
  // When the parent has already triggered a reveal (single audited action),
  // we render the full text with the sensitive spans subtly highlighted
  // rather than bar-masked again. This avoids duplicate reveal affordances.
  const sorted = [...spans].sort((a, b) => a.start - b.start);
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  sorted.forEach((s, i) => {
    if (s.start > cursor) parts.push(<span key={`t${i}`}>{text.slice(cursor, s.start)}</span>);
    parts.push(
      <span key={`h${i}`} className="sg-sensitive-span" title={labelFor(s.category)}>
        {text.slice(s.start, s.end)}
      </span>
    );
    cursor = s.end;
  });
  if (cursor < text.length) parts.push(<span key="tail">{text.slice(cursor)}</span>);
  return <div className="sg-redact is-revealed">{parts}</div>;
}

function MarkerRedact({ text }: { text: string }) {
  // tokens of two kinds: plain text and 【category】 markers
  const tokens: React.ReactNode[] = [];
  const re = /【([^】]+)】/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) tokens.push(<span key={`p${i}`}>{text.slice(last, m.index)}</span>);
    tokens.push(<RedactMarker key={`m${i}`} category={m[1]} />);
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) tokens.push(<span key="tail">{text.slice(last)}</span>);
  return <div className="sg-redact">{tokens}</div>;
}

function RevealChunk({ text, label }: { text: string; label: string }) {
  const [shown, setShown] = useState(false);
  return (
    <span
      className={`sg-redact-chunk ${shown ? "is-shown" : ""}`}
      title={shown ? "Hide" : `Reveal ${label}`}
      onClick={() => setShown((s) => !s)}
    >
      {shown ? <>{text}<EyeIcon off /></> : <><RedactBar /><EyeIcon /></>}
    </span>
  );
}

function RedactMarker({ category }: { category: string }) {
  return (
    <span className="sg-redact-chunk" title={`Redacted: ${labelFor(category)}`}>
      <RedactBar />
    </span>
  );
}

function RedactBar() {
  return <span className="sg-redact-bar">█████</span>;
}

function EyeIcon({ off = false }: { off?: boolean }) {
  return (
    <svg className="sg-redact-eye" viewBox="0 0 16 16" width="11" height="11" aria-hidden>
      <path d="M8 3c3.5 0 6 3.2 6.5 5-0.5 1.8-3 5-6.5 5S2 9.8 1.5 8C2 6.2 4.5 3 8 3z" fill="none" stroke="currentColor" strokeWidth="1.1" />
      <circle cx="8" cy="8" r="1.8" fill="currentColor" />
      {off && <path d="M2 2 L14 14" stroke="currentColor" strokeWidth="1.1" />}
    </svg>
  );
}

function labelFor(cat?: string): string {
  if (!cat) return "Sensitive";
  return titleCaseCategory(cat);
}

const ACRONYMS = new Set([
  "NHS", "CHI", "HN", "MRN", "PII", "DOB", "UK", "US", "EU", "IBAN", "NI",
  "JWT", "AWS", "GCP", "API", "SSN", "URL", "URI", "IP", "ID",
  "LLP", "LLC", "PLC", "LTD", "INC", "OTP", "2FA", "SSH", "PGP", "RSA", "DSA",
]);
function titleCaseCategory(input: string): string {
  return input
    .replace(/_/g, " ")
    .split(/(\s+|-|\/)/)
    .map((part) => {
      if (!part || /^\s+$/.test(part) || part === "-" || part === "/") return part;
      const up = part.toUpperCase();
      if (ACRONYMS.has(up)) return up;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join("");
}
